import { createFileRoute, useLocation, useSearch } from "@tanstack/react-router";
import { useMemo } from "react";
import { ReportBuilderWizard } from "@/components/portal/apps/reporting/custom/ReportBuilder/ReportBuilderWizard";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import type { TemplatizeQueryResponse } from "@/lib/api";
import type { ColumnDefinition, FilterDefinition } from "@/components/portal/apps/reporting/custom/types";

type NewReportSearch = {
  from?: string;
  handoffKey?: string;
  /** Pre-select a datasource (used when launching from the datasource detail page). */
  datasourceId?: string;
  /** Pre-select the report kind (plan D16) — the list's "New statement" entry point. */
  kind?: "grid" | "statement";
};

export const Route = createFileRoute("/portal/apps/reporting/custom/new")({
  component: NewCustomReportPage,
  validateSearch: (search: Record<string, unknown>): NewReportSearch => ({
    from: (search.from as string) || undefined,
    handoffKey: (search.handoffKey as string) || undefined,
    datasourceId: (search.datasourceId as string) || undefined,
    kind: search.kind === "statement" ? "statement" : undefined,
  }),
});

/**
 * Normalise a handoff payload into wizard `initialData`.
 *
 * Two shapes arrive here and they differ in what they DO NOT carry. The legacy
 * templatise handoff brings a query and its columns but no datasource. The
 * promote handoff brings a datasource that already exists (columns and query
 * live on it, server-derived) and therefore carries neither — so every field
 * below is read defensively. Reading `r.columns.map` unguarded crashed the
 * wizard on the promote payload.
 */
function parseTemplate(
  r: Partial<TemplatizeQueryResponse> & Record<string, unknown>,
) {
  return {
    slug: r.slug,
    name: r.name,
    description: r.description,
    query: r.query ?? undefined,
    columns: (r.columns ?? []).map((c): ColumnDefinition => ({
      key: c.key,
      label: c.label,
      type: (c.type as ColumnDefinition["type"]) || "text",
      format: c.format ?? undefined,
      align: (c.align as ColumnDefinition["align"]) ?? undefined,
      width: c.width ?? undefined,
      visible: c.visible,
    })),
    filters: (r.filters ?? []).map((f): FilterDefinition => ({
      key: f.key,
      label: f.label,
      type: (f.type as FilterDefinition["type"]) || "text",
      required: f.required,
      defaultValue: f.defaultValue ?? undefined,
      options: f.options?.map((o) => ({ value: o.value, label: o.label })),
      // Every field the wizard keeps must be listed here — an unlisted one is
      // silently dropped from the handoff.
      optionsSource: f.optionsSource ?? undefined,
    })),
    narration: r.narration as string | undefined,
    dataSourceId: r.dataSourceId as string | undefined,
    dataSourceName: r.dataSourceName as string | undefined,
    logId: r.logId as string | undefined,
    // Promote handoff: the datasource already exists and is pinned (D25).
    datasourceId: r.datasourceId as string | undefined,
    datasourceSlug: r.datasourceSlug as string | undefined,
    datasourceName: r.datasourceName as string | undefined,
    datasourceLocked: Boolean(r.datasourceLocked),
  };
}

function NewCustomReportPage() {
  const { selectedOrganisation } = usePortalData();
  const organisationId = selectedOrganisation?.organisationId ?? "";
  const location = useLocation();
  const { from, handoffKey, datasourceId, kind } = useSearch({ from: Route.fullPath });

  const initialData = useMemo(() => {
    // Sources are tried in priority order — the first that matches wins.
    // Handoffs (1, 2) carry richer state (template, columns, filters) than the
    // bare datasourceId (3), so a `?datasourceId=` query param on a URL that
    // also has a handoff falls through.
    //
    // Source 1: router state (same-tab navigation, legacy path)
    const state = location.state as { fromSqlRag?: TemplatizeQueryResponse } | undefined;
    if (state?.fromSqlRag) {
      return parseTemplate(state.fromSqlRag as TemplatizeQueryResponse & Record<string, unknown>);
    }

    // Source 2: localStorage handoff (new-tab from KB query page)
    if (from === "kb" && handoffKey) {
      try {
        const raw = localStorage.getItem(handoffKey);
        if (raw) {
          localStorage.removeItem(handoffKey);
          return parseTemplate(JSON.parse(raw));
        }
      } catch {
        /* ignore parse errors */
      }
    }

    // Source 3: datasource pre-selection (launched from datasource detail page).
    // Wizard step 1 uses `datasourceId` to highlight/select the row; the
    // datasource's full details (name, columns) are loaded by the picker itself.
    if (datasourceId) {
      return { datasourceId } as { datasourceId: string };
    }

    return undefined;
  }, [location.state, from, handoffKey, datasourceId]);

  // `kind=statement` is additive to whichever source above matched (or
  // none) — the list's "New statement" button links to a bare
  // `?kind=statement` with no other search params. Honoured only when the
  // kill-switch is on — otherwise a hand-typed URL bypasses the flag
  // entirely and a statement report could still be authored with it off
  // (web #1065 item 4).
  const initialDataWithKind = useMemo(
    () =>
      kind === "statement"
        ? { ...initialData, reportKind: "statement" as const }
        : initialData,
    [initialData, kind],
  );

  return (
    <ReportBuilderWizard
      organisationId={organisationId}
      mode="create"
      initialData={initialDataWithKind}
    />
  );
}
